/**
 * RecurrenceOptionBuilder
 *
 * Generates context-aware recurrence options based on event start date,
 * matching Google Calendar's repeat option style.
 */

import type { RecurrenceRule, DayOfWeek } from '@/types/events';

export type RecurrenceOption = {
  id: string;
  label: string;
  rule: RecurrenceRule | null;
};

const DAY_NAMES: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const DAY_CODES: Record<number, DayOfWeek> = {
  0: 'SU',
  1: 'MO',
  2: 'TU',
  3: 'WE',
  4: 'TH',
  5: 'FR',
  6: 'SA',
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const ORDINAL_LABELS: Record<number, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  [-1]: 'last',
};

export class RecurrenceOptionBuilder {
  /**
   * Build context-aware recurrence options for a given start date.
   */
  static buildOptions(startDate: string): RecurrenceOption[] {
    const [year, month, day] = startDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();
    const dayName = DAY_NAMES[dayOfWeek];
    const dayCode = DAY_CODES[dayOfWeek];
    const monthName = MONTH_NAMES[month - 1];
    const ordinalPos = this.getOrdinalWeekOfMonth(date);
    const ordinalLabel = ORDINAL_LABELS[ordinalPos] ?? `${ordinalPos}th`;

    return [
      {
        id: 'never',
        label: 'Does not repeat',
        rule: null,
      },
      {
        id: 'daily',
        label: 'Daily',
        rule: { frequency: 'DAILY', interval: 1, endCondition: { type: 'never' } },
      },
      {
        id: 'weekly',
        label: `Weekly on ${dayName}`,
        rule: {
          frequency: 'WEEKLY',
          interval: 1,
          byDay: [dayCode],
          endCondition: { type: 'never' },
        },
      },
      {
        id: 'monthly-day',
        label: `Monthly on day ${day}`,
        rule: {
          frequency: 'MONTHLY',
          interval: 1,
          byMonthDay: day,
          endCondition: { type: 'never' },
        },
      },
      {
        id: 'monthly-ordinal',
        label: `Monthly on the ${ordinalLabel} ${dayName}`,
        rule: {
          frequency: 'MONTHLY',
          interval: 1,
          byDay: [dayCode],
          bySetPos: ordinalPos,
          endCondition: { type: 'never' },
        },
      },
      {
        id: 'yearly',
        label: `Annually on ${monthName} ${day}`,
        rule: {
          frequency: 'YEARLY',
          interval: 1,
          byMonth: month,
          byMonthDay: day,
          endCondition: { type: 'never' },
        },
      },
      {
        id: 'weekday',
        label: 'Every weekday (Monday to Friday)',
        rule: {
          frequency: 'WEEKLY',
          interval: 1,
          byDay: ['MO', 'TU', 'WE', 'TH', 'FR'],
          endCondition: { type: 'never' },
        },
      },
      {
        id: 'custom',
        label: 'Custom...',
        rule: null, // Sentinel: triggers custom picker
      },
    ];
  }

  /**
   * Compute the ordinal position of a weekday within its month.
   * Returns 1-4, or -1 if the weekday is the last occurrence in the month.
   */
  static getOrdinalWeekOfMonth(date: Date): number {
    const dayOfMonth = date.getDate();
    const position = Math.ceil(dayOfMonth / 7);

    // Check if this is the last occurrence of this weekday in the month
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const nextWeekDay = dayOfMonth + 7;

    if (nextWeekDay > daysInMonth) {
      return -1; // Last occurrence
    }

    return position;
  }
}
